import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, BookOpen, ArrowRight } from 'lucide-react';
import { useOs2, money, Fade } from './os2ui';
import { OsCard, AnimatedNumber, Skeleton } from '../../os3/theme';

/* Executive Overview — data storytelling: the story of the period, told with real numbers. */

const fmtDay = (iso, isEs) => new Date(`${iso}T12:00:00`).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { day: 'numeric', month: 'long' });

function DeltaChip({ value, isEs }) {
  if (value == null) return <span className="text-[10.5px] font-bold" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'sin período previo' : 'no prior period'}</span>;
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-black tabular-nums"
      style={{ background: up ? 'rgba(52,211,153,0.14)' : 'rgba(248,113,113,0.14)', color: up ? '#10B981' : '#F87171' }}>
      <Icon className="w-3 h-3" /> {up ? '+' : ''}{value}%
    </span>
  );
}

function Sparkline({ points, color = '#F77F11', height = 56, dates, format = (v) => v.toLocaleString(), isEs }) {
  const [hi, setHi] = React.useState(null);
  const w = 260;
  const max = Math.max(1, ...points);
  const step = w / Math.max(1, points.length - 1);
  const y = (v) => height - 4 - (v / max) * (height - 10);
  const line = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${w},${height} L0,${height} Z`;
  const gid = `sg-${color.replace('#', '')}`;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left) / r.width) * (points.length - 1));
    setHi(Math.max(0, Math.min(points.length - 1, i)));
  };
  const dateLabel = hi != null && dates?.[hi]
    ? new Date(`${String(dates[hi]).slice(0, 10)}T12:00:00`).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })
    : '';
  return (
    <div className="relative" onMouseLeave={() => setHi(null)}>
      <svg viewBox={`0 0 ${w} ${height}`} className="w-full cursor-crosshair" style={{ height }} preserveAspectRatio="none" onMouseMove={onMove} aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {hi != null && (
        <g>
          <line x1={(hi * step).toFixed(1)} x2={(hi * step).toFixed(1)} y1="0" y2={height} stroke="#9ca3af" strokeDasharray="2,2" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
          <circle cx={(hi * step).toFixed(1)} cy={y(points[hi]).toFixed(1)} r="3.5" fill={color} stroke="#fff" strokeWidth="1.2" />
        </g>
      )}
      </svg>
      {hi != null && (
        <div className="pointer-events-none absolute -top-9 z-20 rounded-lg bg-gray-900 text-white text-[10.5px] px-2 py-1 shadow-xl whitespace-nowrap"
          style={{ left: `${(hi / Math.max(1, points.length - 1)) * 100}%`, transform: `translateX(${hi > points.length / 2 ? '-105%' : '5%'})` }}
          data-testid="spark-tooltip">
          <b className="capitalize">{dateLabel}</b>{dateLabel && ' · '}<b className="tabular-nums">{format(points[hi])}</b>
        </div>
      )}
    </div>
  );
}

function TrendCard({ title, points, dates, total, delta, color, isEs, format = (v) => v.toLocaleString(), testid, to, linkLabel }) {
  return (
    <Link to={to} className="block h-full group" data-testid={testid}>
      <OsCard className="p-4 h-full transition-all group-hover:-translate-y-0.5 group-hover:shadow-md">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>{title}</p>
          <DeltaChip value={delta} isEs={isEs} />
        </div>
        <p className="mt-1 text-[22px] font-black tabular-nums" style={{ color: 'var(--os-ink)' }}>
          <AnimatedNumber value={total} format={format} />
        </p>
        <div className="mt-2"><Sparkline points={points} dates={dates} color={color} format={format} isEs={isEs} /></div>
        <p className="mt-2 text-[11px] font-bold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color }}>
          {linkLabel} <ArrowRight className="w-3 h-3" />
        </p>
      </OsCard>
    </Link>
  );
}

export default function OverviewStory({ isEs }) {
  const { data: d } = useOs2('/overview/story');
  if (!d) {
    return (
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-36" />)}
      </div>
    );
  }
  const t = d.totals || {};
  const dl = d.deltas || {};
  const daily = d.daily || [];
  const revUp = (dl.revenue ?? 0) >= 0;
  const topSrc = (d.top_sources || [])[0];

  const storyEs = (
    <>
      En los últimos <b style={{ color: 'var(--os-ink)' }}>{d.window_days} días</b> Kitxens ingresó{' '}
      <b style={{ color: 'var(--os-accent)' }}>{money(t.revenue)}</b>
      {dl.revenue != null && <> ({revUp ? '▲' : '▼'} <b style={{ color: revUp ? '#10B981' : '#F87171' }}>{Math.abs(dl.revenue)}%</b> vs el período anterior)</>}.
      {d.best_day && <> El mejor día fue el <b style={{ color: 'var(--os-ink)' }}>{fmtDay(d.best_day.date, true)}</b> con {money(d.best_day.revenue)}.</>}
      {topSrc && <> La fuente principal de leads: <b style={{ color: 'var(--os-ink)' }}>{topSrc.source}</b> ({topSrc.count}).</>}
      {t.reports > 0 && <> Se desbloquearon <b style={{ color: 'var(--os-ink)' }}>{t.reports}</b> reportes.</>}
    </>
  );
  const storyEn = (
    <>
      Over the last <b style={{ color: 'var(--os-ink)' }}>{d.window_days} days</b> Kitxens collected{' '}
      <b style={{ color: 'var(--os-accent)' }}>{money(t.revenue)}</b>
      {dl.revenue != null && <> ({revUp ? '▲' : '▼'} <b style={{ color: revUp ? '#10B981' : '#F87171' }}>{Math.abs(dl.revenue)}%</b> vs the previous period)</>}.
      {d.best_day && <> Best day: <b style={{ color: 'var(--os-ink)' }}>{fmtDay(d.best_day.date, false)}</b> with {money(d.best_day.revenue)}.</>}
      {topSrc && <> Top lead source: <b style={{ color: 'var(--os-ink)' }}>{topSrc.source}</b> ({topSrc.count}).</>}
      {t.reports > 0 && <> <b style={{ color: 'var(--os-ink)' }}>{t.reports}</b> reports unlocked.</>}
    </>
  );

  return (
    <Fade delay={0.02}>
      <div className="mt-4" data-testid="ov-story">
        <OsCard className="p-5 relative overflow-hidden" glow data-testid="ov-story-narrative">
          <span className="absolute inset-x-0 top-0 h-[2px]" style={{ background: 'linear-gradient(90deg,transparent,#F77F11,transparent)' }} aria-hidden />
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-accent)' }}>
            <BookOpen className="w-3.5 h-3.5" /> {isEs ? 'La historia del período' : 'The story of the period'}
          </p>
          <p className="mt-2 text-[15.5px] leading-relaxed max-w-4xl" style={{ color: 'var(--os-ink-soft)' }}>
            {isEs ? storyEs : storyEn}
          </p>
        </OsCard>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <TrendCard title={isEs ? `Ingresos · ${d.window_days}d` : `Revenue · ${d.window_days}d`} testid="ov-trend-revenue"
            points={daily.map((x) => x.revenue)} dates={daily.map((x) => x.date)} total={t.revenue} delta={dl.revenue} color="#F77F11" isEs={isEs}
            format={(v) => money(v)} to="/admin/ai-os/finance" linkLabel={isEs ? 'Ver Finanzas' : 'View Finance'} />
          <TrendCard title={`Leads · ${d.window_days}d`} testid="ov-trend-leads"
            points={daily.map((x) => x.leads)} dates={daily.map((x) => x.date)} total={t.leads} delta={dl.leads} color="#38BDF8" isEs={isEs}
            to="/admin/ai-os/pipeline?tab=sales" linkLabel={isEs ? 'Ver Leads' : 'View Leads'} />
          <TrendCard title={`Scans · ${d.window_days}d`} testid="ov-trend-scans"
            points={daily.map((x) => x.scans)} dates={daily.map((x) => x.date)} total={t.scans} delta={dl.scans} color="#34D399" isEs={isEs}
            to="/admin/ai-os/growth#scans" linkLabel={isEs ? 'Ver Scans' : 'View Scans'} />
        </div>
      </div>
    </Fade>
  );
}
